/**
 * Кастомные ошибки HTTP запросов с категориями для retry logic
 * Используется для GraphQL, REST API и любых других HTTP запросов
 */

/**
 * Категории HTTP ошибок
 */
export enum HttpErrorType {
    /** Таймаут запроса */
    TIMEOUT = 'TIMEOUT',
    /** Сетевая ошибка */
    NETWORK = 'NETWORK',
    /** Ошибка сервера (5xx) */
    SERVER_ERROR = 'SERVER_ERROR',
    /** Пустой ответ от сервера */
    EMPTY_RESPONSE = 'EMPTY_RESPONSE',
    /** Невалидный JSON */
    JSON_PARSE_ERROR = 'JSON_PARSE_ERROR',
    /** Ошибка клиента (4xx) - общая */
    CLIENT_ERROR = 'CLIENT_ERROR',
    /** Ошибка доступа (401, 403) */
    ACCESS_ERROR = 'ACCESS_ERROR',
    /** Ошибка валидации данных */
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    /** Ресурс не найден (404) */
    NOT_FOUND = 'NOT_FOUND',
    /** Конфликт данных (409) */
    CONFLICT = 'CONFLICT',
    /** Rate limiting (429) */
    RATE_LIMIT = 'RATE_LIMIT',
    /** Запрос был отменен */
    ABORTED = 'ABORTED',
    /** Неизвестная ошибка */
    UNKNOWN = 'UNKNOWN'
}

/**
 * Опции для создания HttpError
 */
export interface HttpErrorOptions {
    type: HttpErrorType | string
    isRetryable?: boolean
    statusCode?: number
    details?: any
    name?: string
    alertShow?: boolean  // Показывать ли alert пользователю
    alertMessage?: string  // Кастомное сообщение для alert (если не задано - используется message)
    targetShow?: boolean  // Показывать ли сообщение в целевом месте причины ошибки (напирмер поле ввода)
    targetMessage?: string  // Кастомное сообщение для целевого места
}

/**
 * Базовый класс для HTTP ошибок
 */
export class HttpError extends Error {
    public type?: HttpErrorType | string
    public isRetryable?: boolean
    public statusCode?: number
    public details?: any
    public alertShow?: boolean
    public alertMessage?: string
    public targetShow?: boolean
    public targetMessage?: string

    constructor(message: string, options: HttpErrorOptions) {
        super(message)
        this.fill(options)
        // Поддержка stack trace в разных браузерах
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, HttpError)
        }
    }

    fill(options: HttpErrorOptions) {
        this.name = options.name ?? 'HttpError'
        this.type = options.type
        this.isRetryable = options.isRetryable ?? false
        this.statusCode = options.statusCode
        this.details = options.details
        this.alertShow = options.alertShow ?? false
        this.alertMessage = options.alertMessage
        this.targetShow = options.targetShow ?? false
        this.targetMessage = options.targetMessage
    }

    getTargetMessage(): string {
        return this.targetMessage ?? this.message
    }

    getAlertMessage(): string {
        return this.alertMessage ?? this.message
    }

    /**
     * Преобразовать в объект для логирования
     */
    toJSON() {
        return {
            name: this.name,
            message: this.message,
            type: this.type,
            isRetryable: this.isRetryable,
            statusCode: this.statusCode,
            details: this.details,
            alert: this.alertShow,
            alertMessage: this.alertMessage
        }
    }
}

/**
 * Ошибка таймаута
 */
export class TimeoutError extends HttpError {
    constructor(timeout: number) {
        super(`Request timeout after ${timeout}ms`, {
            type: HttpErrorType.TIMEOUT,
            isRetryable: true,
            alertShow: false,
            alertMessage: 'Превышено время ожидания. Попробуйте еще раз.',
            details: {timeout}
        })
        this.name = 'TimeoutError'
    }
}

/**
 * Сетевая ошибка
 */
export class NetworkError extends HttpError {
    constructor(message: string, originalError?: any) {
        super(message, {
            type: HttpErrorType.NETWORK,
            isRetryable: true,
            alertShow: false,
            alertMessage: 'Ошибка сети. Проверьте подключение к интернету.',
            details: {originalError: originalError?.message}
        })
        this.name = 'NetworkError'
    }
}

/**
 * Ошибка сервера (5xx)
 */
export class ServerError extends HttpError {
    constructor(statusCode: number, statusText: string, body?: string) {
        super(`${statusCode} ${statusText}${body ? ': ' + body : ''}`, {
            type: HttpErrorType.SERVER_ERROR,
            isRetryable: true,
            alertShow: true,
            alertMessage: 'Ошибка сервера. Попробуйте позже.',
            statusCode,
            details: {statusText, body}
        })
        this.name = 'ServerError'
    }
}

/**
 * Ошибка клиента (4xx)
 */
export class ClientError extends HttpError {
    constructor(statusCode: number, statusText: string, body?: string) {
        super(`${statusCode} ${statusText}${body ? ': ' + body : ''}`, {
            type: HttpErrorType.CLIENT_ERROR,
            isRetryable: false,
            alertShow: false,  // Клиентские ошибки обычно не показываем
            statusCode,
            details: {statusText, body}
        })
        this.name = 'ClientError'
    }
}

/**
 * Пустой ответ от сервера
 */
export class EmptyResponseError extends HttpError {
    constructor(reason: string = 'Server returned empty response') {
        super(reason, {
            type: HttpErrorType.EMPTY_RESPONSE,
            isRetryable: true,
            alertShow: true,
            alertMessage: 'Сервер вернул пустой ответ. Попробуйте еще раз.',
            details: {reason}
        })
        this.name = 'EmptyResponseError'
    }
}

/**
 * Ошибка парсинга JSON
 */
export class JSONParseError extends HttpError {
    constructor(text: string, originalError?: any) {
        super(`Invalid JSON response: ${text.substring(0, 100)}`, {
            type: HttpErrorType.JSON_PARSE_ERROR,
            isRetryable: false,
            alertShow: true,
            alertMessage: 'Ошибка обработки ответа сервера.',
            details: {
                responsePreview: text.substring(0, 200),
                originalError: originalError?.message
            }
        })
        this.name = 'JSONParseError'
    }
}

/**
 * Запрос был отменен
 */
export class AbortedError extends HttpError {
    constructor(reason?: string) {
        super(reason || 'Request was aborted', {
            type: HttpErrorType.ABORTED,
            isRetryable: false,
            alertShow: false,  // Отмененные запросы не показываем
            details: {reason}
        })
        this.name = 'AbortedError'
    }
}

/**
 * Проверить, является ли ошибка повторяемой
 */
export const isRetryableError = (error: any): boolean => {
    // Если это наша кастомная ошибка - используем флаг
    if (error instanceof HttpError) {
        return error.isRetryable ?? false
    }

    // Проверяем по категории (если есть)
    if (error.type) {
        const retryableTypes = [
            HttpErrorType.TIMEOUT,
            HttpErrorType.NETWORK,
            HttpErrorType.SERVER_ERROR,
            HttpErrorType.EMPTY_RESPONSE,
            HttpErrorType.RATE_LIMIT
        ]
        return retryableTypes.includes(error.type)
    }

    // Fallback на старую логику для обычных ошибок
    if (error.message?.includes('Request timeout')) {
        return true
    }

    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        return true
    }

    if (error.message?.match(/^5\d{2}/)) {
        return true
    }

    if (error.message?.includes('Empty response')) {
        return true
    }

    return false
}

/**
 * Создать ошибку из HTTP ответа
 */
export const createErrorFromResponse = (
    statusCode: number,
    statusText: string,
    body?: string
): HttpError => {
    if (statusCode >= 500) {
        return new ServerError(statusCode, statusText, body)
    } else if (statusCode === 429) {
        return new HttpError(
            `${statusCode} ${statusText}${body ? ': ' + body : ''}`,
            {
                type: HttpErrorType.RATE_LIMIT,
                isRetryable: true,
                alertShow: true,
                alertMessage: 'Слишком много запросов. Попробуйте позже.',
                statusCode,
                details: {statusText, body}
            }
        )
    } else if (statusCode === 409) {
        return new HttpError(
            `${statusCode} ${statusText}${body ? ': ' + body : ''}`,
            {
                type: HttpErrorType.CONFLICT,
                isRetryable: false,
                alertShow: true,
                alertMessage: 'Конфликт данных. Обновите страницу и попробуйте снова.',
                statusCode,
                details: {statusText, body}
            }
        )
    } else if (statusCode === 404) {
        return new HttpError(
            `${statusCode} ${statusText}${body ? ': ' + body : ''}`,
            {
                type: HttpErrorType.NOT_FOUND,
                isRetryable: false,
                alertShow: false,  // 404 обычно не показываем
                statusCode,
                details: {statusText, body}
            }
        )
    } else if (statusCode === 401 || statusCode === 403) {
        return new HttpError(
            `${statusCode} ${statusText}${body ? ': ' + body : ''}`,
            {
                type: HttpErrorType.ACCESS_ERROR,
                isRetryable: false,
                alertShow: true,
                alertMessage: statusCode === 401 ? 'Требуется авторизация' : 'Доступ запрещен',
                statusCode,
                details: {statusText, body}
            }
        )
    } else if (statusCode === 400 || statusCode === 422) {
        return new HttpError(
            `${statusCode} ${statusText}${body ? ': ' + body : ''}`,
            {
                type: HttpErrorType.VALIDATION_ERROR,
                isRetryable: false,
                alertShow: true,
                alertMessage: body || 'Ошибка валидации данных',
                statusCode,
                details: {statusText, body}
            }
        )
    } else if (statusCode >= 400) {
        return new ClientError(statusCode, statusText, body)
    } else {
        return new HttpError(
            `${statusCode} ${statusText}${body ? ': ' + body : ''}`,
            {
                type: HttpErrorType.UNKNOWN,
                isRetryable: false,
                alertShow: false,
                statusCode,
                details: {statusText, body}
            }
        )
    }
}
